import { TQuickReplyItem, TQuickReplySessionState } from '../types/quickReply';
import parseJSON from './validateStorageVariable';

const STORAGE_PREFIX = 'quick_reply';

/** Matches backend QUICK_REPLY_ANSWER_MESSAGE_KIND for typing-bubble correlation. */
const QUICK_REPLY_ANSWER_MESSAGE_KIND = 'QUICK_REPLY_ANSWER';

/** Keep ••• visible at least this long, even if the PubNub answer arrives sooner. */
const QUICK_REPLY_TYPING_MIN_MS = 1000;

/** Safety net if the HTTP request hangs or the matching PubNub event never arrives. */
const QUICK_REPLY_ANSWER_FAIL_TIMEOUT_MS = 10000;

const emptySessionState = (): TQuickReplySessionState => ({
  consumedIds: [],
});

const getQuickReplyStorageKey = (channelId: string): string =>
  `${STORAGE_PREFIX}:${channelId}`;

const loadQuickReplySession = (channelId: string): TQuickReplySessionState => {
  try {
    const raw = sessionStorage.getItem(getQuickReplyStorageKey(channelId));
    if (!raw) return emptySessionState();
    const parsed = parseJSON(raw) as Partial<TQuickReplySessionState>;
    if (!parsed || !Array.isArray(parsed.consumedIds)) {
      return emptySessionState();
    }
    return {
      consumedIds: parsed.consumedIds.filter((id) => typeof id === 'string'),
    };
  } catch {
    return emptySessionState();
  }
};

const saveQuickReplySession = (
  channelId: string,
  state: TQuickReplySessionState,
): void => {
  try {
    sessionStorage.setItem(
      getQuickReplyStorageKey(channelId),
      JSON.stringify({ consumedIds: state.consumedIds }),
    );
  } catch {
    // sessionStorage may be unavailable; keep in-memory UI state only
  }
};

/** Persist a consumed chip id for this channel (history itself is on PubNub). */
const markQuickReplyConsumed = (
  channelId: string,
  id: string,
): TQuickReplySessionState => {
  const current = loadQuickReplySession(channelId);
  if (current.consumedIds.includes(id)) return current;
  const next: TQuickReplySessionState = {
    consumedIds: [...current.consumedIds, id],
  };
  saveQuickReplySession(channelId, next);
  return next;
};

const remainingQuickReplyTypingHideMs = (
  startedAtMs: number,
  nowMs: number = Date.now(),
): number => Math.max(0, QUICK_REPLY_TYPING_MIN_MS - (nowMs - startedAtMs));

type TQuickReplyAnswerMessageData = {
  messageKind?: string;
  quickReplyId?: string;
};

const getQuickReplyAnswerMessageData = (
  message: unknown,
): TQuickReplyAnswerMessageData | undefined => {
  if (!message || typeof message !== 'object') return undefined;
  const data = (message as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return undefined;
  return data as TQuickReplyAnswerMessageData;
};

/** True when a live PubNub message is the pending quick-reply answer. */
const isMatchingQuickReplyAnswerEvent = (
  message: unknown,
  pendingQuickReplyId: string | null,
): boolean => {
  if (!pendingQuickReplyId) return false;
  const data = getQuickReplyAnswerMessageData(message);
  return (
    data?.messageKind === QUICK_REPLY_ANSWER_MESSAGE_KIND &&
    data.quickReplyId === pendingQuickReplyId
  );
};

/** Hide ••• only after the matching answer event and the minimum duration. */
const canHideQuickReplyTyping = ({
  eventReceived,
  startedAtMs,
  nowMs = Date.now(),
}: {
  eventReceived: boolean;
  startedAtMs: number;
  nowMs?: number;
}): boolean =>
  eventReceived && remainingQuickReplyTypingHideMs(startedAtMs, nowMs) === 0;

/**
 * Chip `disabled` / `isQuickReplyBlocked` can lag a paint. Use the live in-flight
 * flag and answering id so a second tap cannot start another send.
 */
const isQuickReplyTapLocked = (
  inFlight: boolean,
  answeringQuickReplyId: string | null,
): boolean => inFlight || answeringQuickReplyId != null;

/** Do not clear a different chip's shared answering timers from a stale catch. */
const shouldStopAnsweringAfterQuickReplyFailure = (
  answeringQuickReplyId: string | null,
  failedQuickReplyId: string,
): boolean => answeringQuickReplyId === failedQuickReplyId;

/** Chips still shown: not consumed yet. */
const selectRemainingQuickReplies = (
  items: TQuickReplyItem[],
  consumedIds: string[],
): TQuickReplyItem[] => items.filter((item) => !consumedIds.includes(item.id));

export {
  QUICK_REPLY_ANSWER_FAIL_TIMEOUT_MS,
  QUICK_REPLY_ANSWER_MESSAGE_KIND,
  QUICK_REPLY_TYPING_MIN_MS,
  canHideQuickReplyTyping,
  getQuickReplyStorageKey,
  isMatchingQuickReplyAnswerEvent,
  isQuickReplyTapLocked,
  loadQuickReplySession,
  shouldStopAnsweringAfterQuickReplyFailure,
  markQuickReplyConsumed,
  remainingQuickReplyTypingHideMs,
  saveQuickReplySession,
  selectRemainingQuickReplies,
};
